import axios from 'axios';

import store from '../../store/store';
import ExemptionManager from '../manager/exemptionManager';
import {forceFileDownload} from '../../utils/utils';

export const fetchPartialBeneficiaries = async () => {
    const { data } = await axios.get('/beneficiaries');

    return data.filter((beneficiary) => {
        return ['b2', 'b3'].includes(beneficiary.code);
    });
};

export const fetchPartialBeneficiariesByAccessToken = async (accessToken) => {
    const { data } = await axios.get(`/beneficiaries/${ accessToken }`);

    return data.filter((beneficiary) => {
        return ['b2', 'b3'].includes(beneficiary.code);
    });
};

export const fetchReasons = async (config) => {
    return await axios.get('/reasons', config);
};

export const fetchExemptionRenewReasons = async (exemptionId, config) => {
    return await axios.get(`/exemptions/${exemptionId}/renew-reasons`, config);
};

export const fetchDueReasonsByAccessToken = async (accessToken, dueId, enterpriseUuid) => {
    return await axios.get(`/reasons/${ accessToken }`, {
        params: {
            dueId,
            enterpriseUuid,
        }
    });
};

export const fetchEnterpriseReasonsByAccessToken = async (accessToken, enterpriseUuid) => {
    return await axios.get(`/reasons/${ accessToken }`, {
        params: {
            enterpriseUuid,
            enterpriseOptionalReasons: 1,
        }
    });
};

export const fetchReasonById = async (id, enterpriseUuid = null) => {
    const config = enterpriseUuid ? { params: { enterpriseUuid } } : {};
    return await axios.get(`reasons/${ id }`, config);
};

export const fetchReasonByExemptionId = async (exemptionId) => {
    return await axios.get(`exemptions/${ exemptionId }/reason`);
};

export const fetchReasonByIdAndAccessToken = async (id, accessToken) => {
    return await axios.get(`reasons/${ id }/${ accessToken }`);
};

export const fetchCreateLegalInformation = async (reason) => {
    if (store.state.exemption.createLegalInformation) {
        return;
    }

    const { data } =  await axios.get(`/reasons/${ reason }/legal-information`);
    store.commit('SET_CREATE_LEGAL_INFORMATION', data);
};

export const fetchReasonLegalInformation = async (id) => {
    return await axios.get(`/reasons/${ id }/legal-information`);
};

export const fetchContactTypes = async () => {
    if (store.state.exemption.contractTypes && store.state.exemption.contractTypes.length > 0) {
        return;
    }

    const { data } = await axios.get('/contract-types');
    store.commit('SET_CONTRACT_TYPES', data);
};

export const fetchExemptionById = async (id) => {
    return await axios.get(`/exemptions/${ id }`);
}

export const fetchExemptionByAccessToken = async (token) => {
    return await axios.get(`/employees/exemptions/${ token }`);
}

export const initExemptionByAccessToken = async (exemptionInitToken, clientSlug) => {
    return await axios.post('/employees/exemptions/init', {
        token: exemptionInitToken,
        slug: clientSlug,
        responseType: 'json',
    }, {
        maxRedirects: 0,
    });
}

export const fetchReviewedExemptions = async (pagination, sort, filters, signal) => {
    return await axios.get('/exemptions', {
        signal,
        params: {
            page: pagination.currentPage,
            pageLimit: pagination.itemsPerPage,
            'sort[field]': sort.field,
            'sort[direction]': sort.direction,
            ...Object.entries(filters).reduce((acc, [field, value]) => ({
                ...acc,
                [`filters[${field}]`]: value,
            }), {}),
        }
    });
};

export const fetchListExemptionsExportRequests = async (pagination, sort, signal) => {
    return await axios.get('/exemptions/export/list', {
        signal,
        params: {
            page: pagination.currentPage,
            pageLimit: pagination.itemsPerPage,
            'sort[field]': sort.field,
            'sort[direction]': sort.direction
        }
    });
};

export const downloadExemptionsListExport = async (exportId) => {
    const url = `exemptions/export/${exportId}/download`;

    await forceFileDownload(url, '', '', {}, 30000);
};

export const removeExemptionsListExport = async (exportId) => {
    const url = `exemptions/export/${exportId}`;

    return await axios.delete(url);
};

export const requireListExemptionsExport = async (sort, filters, withAttachments = false) => {
    return await axios.post('/exemptions/export/list', {
        filters,
        sort: {
            field: sort.field,
            direction: sort.direction,
        },
        ...(withAttachments && { withAttachments: 1 }),
    });
};

export const fetchRequestedExemptions = async (pagination, status) => {
    return await axios.get('/exemptions', {
        params: {
            page: pagination.currentPage,
            pageLimit: pagination.itemsPerPage,
            ...(status ? { 'filters[status]': status } : { 'filters[statusesPackage]': 'requested' }),
            'sort[direction]': 'desc',
            enterpriseUuid: store.getters.enterpriseUuid
        }
    });
};

export const createExemption = async (formData) => {
    return await axios.post(
        '/exemptions',
        await ExemptionManager.createFromFormData(formData),
        {
            params: {
                enterpriseUuid: store.getters.enterpriseUuid
            }
        }
    );
};

export const updateToRenewExemption = async (exemptionId, formData) => {
    return await axios.put(
        `/exemptions/${ exemptionId }`,
        await ExemptionManager.createFromFormData(formData)
    );
};

export const patchExemptionPersonalData = async (exemptionId, payload) => {
    return await axios.patch(`/exemptions/${ exemptionId }`, payload);
};

export const updateExemption = async (exemptionId, formData) => {
    return await axios.put(
        `exemptions/${ exemptionId }`,
        await ExemptionManager.createEditRequestFromFormData(formData)
    );
};

export const employeeConfirmByAccessToken = async (accessToken, formData) => {
    return await axios.put(
        `/employees/exemptions/${ accessToken }/confirm`,
        await ExemptionManager.createFromFormData(formData)
    );
};

export const abortExemptionRenew = async (accessToken) => {
    return await axios.post(`/employees/exemptions/${ accessToken }/abort`);
};

export const sendEmailToEmployee = async (exemptionId) => {
    return await axios.post(`/exemptions/${ exemptionId }/mail/send-update-link`);
};

export const downloadExemption = async (exemptionId) => {
    await forceFileDownload(`/exemptions/${ exemptionId }/download`, 'Exemption.pdf');
};

export const downloadExemptionExportFileById = async (id) => {
    await forceFileDownload(`/exemptions/item-export/${ id }/download`, 'Exemption_URSSAF.pdf');
};

export const createExemptionExportRequest = async () => {
    return await axios.post(
        `/exemptions/collection-export`,
        { statuses: ['validated', 'rejected', 'aborted', 'archived'] },
        {
            params: {
                enterpriseUuid: store.getters.enterpriseUuid
            }
        }
    );
};

export const fetchExemptionExportRequestById = async (id) => {
    return await axios.get(`/exemptions/collection-export/${ id }`);
};

export const downloadExemptionExportDocument = async (id) => {
    await forceFileDownload(
        `exemptions/collection-export/${ id }/download`,
        'Exemptions_URSSAF.pdf'
    );
};

export const resendEmployeeExemptionUpdateEmail = async (id) => {
    return await axios.post(`/exemptions/${ id }/mail/resend-update-link`);
};

export const extendExemptionDeadline = async (id, newDeadlineDate, sendReminderEmail) => {
    return await axios.post(`/exemptions/${ id }/extend-deadline`, {
        newDeadlineDate,
        sendReminderEmail,
    });
};

export const approveExemptionById = async (id) => {
    return await axios.post(`/exemptions/${ id }/approve`,);
};

export const rejectExemptionById = async (id, formData) => {
    return await axios.post(`/exemptions/${ id }/reject`, formData);
};

export const fetchEnterprisesExemptionStatistics = async(enterpriseIds) => {
    return await axios.get('/exemptions/enterprise-statistics', { params : { enterpriseIds } });
}

export const fetchEarlyTerminationReasons = async () => {
    return await axios.get('/early-termination-reasons');
};

export const fetchCivilities = async () => {
    return await axios.get('/civilities');
};

export const terminateEarlyExemptionById = async (id, earlyTerminationReason, earlyTerminationEndDate) => {
    return await axios.post(`/exemptions/${ id }/terminate-early`, { earlyTerminationReason, earlyTerminationEndDate });
};
